<template>
  <form
    :ref="id"
    :id="id"
    :action="url"
    class="Dropzone dropzone vue-dropzone">
    <slot />
  </form>
</template>

<script>
import { actions, getters } from '@utils'
import Dropzone from 'dropzone'

const icon = name => `<i class="i-${name}"></i>`

export default {
  props: {
    id: {
      required: true,
      type: String,
    },
    url: {
      required: true,
      type: String,
    },
    dropzoneOptions: {
      default: () => ({}),
      type: Object,
    },
  },
  data() {
    return {
      dropzone: {},
    }
  },
  computed: {
    //     previewTemplate() {
    //       return `<div class="dz-preview dz-file-preview">
    //   <div class="dz-image">
    //   <img data-dz-thumbnail /></div>
    //   <div class="dz-details">
    //     <div class="dz-size"><span data-dz-size></span></div>
    //     <div class="dz-filename"><span data-dz-name></span></div>
    //   </div>
    //   <div class="dz-progress"><span class="dz-upload" data-dz-uploadprogress></span></div>
    //   <div class="dz-error-message"><span data-dz-errormessage></span></div>
    //   <div class="dz-success-mark">${this.doneIcon}</div>
    //   <div class="dz-error-mark">${this.errorIcon}</div>
    // </div>`
    //     },
    languageSettings() {
      const settings = {
        dictDefaultMessage: `<br>${this.dropzoneOptions.dictDefaultMessage || this.$t('dropFiles')}`,
        dictCancelUpload: this.$t('cancel'),
        dictCancelUploadConfirmation: this.$t('cancelConfirm'),
        dictFallbackMessage: this.$t('ie'),
        dictFallbackText: this.$t('fallback'),
        dictFileTooBig: this.$t('tooBig'),
        dictInvalidFileType: this.$t('invalidType'),
        dictMaxFilesExceeded: this.$t('tooMany'),
        dictRemoveFile: this.$t('remove'),
        dictRemoveFileConfirmation: null,
        dictResponseError: this.$t('serverError'),
      }

      if (this.useCustomDropzoneOptions && this.dropzoneOptions.language) {
        Object
          .keys(this.dropzoneOptions.language)
          .forEach((attrname) => {
            settings[attrname] = this.dropzoneOptions.language[attrname]
          })
      }

      return settings
    },
    cloudIcon() {
      return icon('file')
    },
    doneIcon() {
      return icon('check')
    },
    errorIcon() {
      return icon('cross')
    },
    ...getters('authHeaders'),
  },
  methods: {
    removeFile(file) {
      this.dropzone.removeFile(file)
    },
    addFile(file) {
      this.dropzone.addFile(file)
    },
    removeAllFiles(cancelIfNecessary) {
      this.dropzone.removeAllFiles(cancelIfNecessary)
    },
    async paste(event) {
      // Checks if CTRL+V pressed
      if (!((event.keyCode === 86 || event.which === 86) && event.ctrlKey)) return
      const clipboardItems = await navigator.clipboard.read()
      const files = await Promise.all(clipboardItems
        .map(item => {
          if (!item.types.includes('image/png')) return
          return item.getType('image/png')
        })
        .filter(Boolean))
      files.forEach(file => {
        file.lastModifiedDate = new Date()
        file.name = 'image.png'
        this.dropzone.addFile(file)
      })
    },
    ...actions(),
  },
  mounted() {
    if (this.$isServer) {
      return
    }

    Dropzone.autoDiscover = false

    if (this.confirm) {
      Dropzone.confirm = this.dropzoneOptions.confirm
    }

    document
      .getElementById(this.id).addEventListener('mouseover', () => {
        document.addEventListener('keydown', this.paste)
      })

    document
      .getElementById(this.id).addEventListener('mouseout', () => {
        document.removeEventListener('keydown', this.paste, false)
      })

    const vm = this
    const element = document.getElementById(this.id)
    this.dropzone = new Dropzone(element, {
      // previewTemplate: this.previewTemplate,
      id: vm.id,
      url: vm.url,
      ...this.dropzoneOptions,
      ...this.languageSettings,
      headers: this.authHeaders,
      dictDefaultMessage: this.cloudIcon + this.languageSettings.dictDefaultMessage,
    })

    this.dropzone.on('error', (file, error, xhr) => {
      if (error !== 'Upload canceled.') {
        this.ERROR(error)
      }
    })

    ;[
      'addedFile',
      'thumbnail',
      'filesAdded',
      'removedFile',
      'success',
      'successMultiple',
      'error',
      'sending',
      'sendingMultiple',
      'queueComplete',
      'totalUploadProgress',
    ]
      .map(event => [
        event.toLowerCase(),
        event
          .split(/(?=[A-Z])/)
          .join('-')
          .toLowerCase(),
      ])
      .forEach((event) => {
        this.dropzone.on(event[0], (...arguments_) => {
          this.$emit(`vdropzone-${event[1]}`, ...arguments_)
        })
      })

    // Handle the dropzone events
    this.dropzone.on('addedfile', (file) => {
      this.$emit('isLoading', true)
      if (this.duplicateCheck && this.files.length) {
        this.files
          .filter(existingFile => existingFile.name === file.name)
          .forEach((existingFile) => {
            this.removeFile(file)
            this.$emit('duplicate-file', file)
          })
      }
    })
    this.dropzone.on('success', () => {
      this.$emit('isLoading', false)
      document.removeEventListener('keydown', this.paste, false)
    })
    this.dropzone.on('error', () => {
      this.$emit('isLoading', false)
      document.removeEventListener('keydown', this.paste, false)
    })
    this.$emit('vdropzone-mounted')
  },
  beforeDestroy() {
    const { dropzone } = this

    if (dropzone && typeof dropzone.destroy === 'function') {
      dropzone.off()
      dropzone.destroy()
    }
  },
}
</script>

<style lang="scss">
@import '~dropzone/dist/dropzone.css';

.vue-dropzone {
  border: 2px solid $grey-lightest;
  color: $grey;
  font-family: $font-body;
  letter-spacing: 0.2px;
  transition: background-color 0.2s linear;

  i {
    color: $grey;
  }

  .dz-message {
    align-items: center;
    display: flex;
    height: 100%;
    justify-content: center;
    margin: 0;
    min-height: 10rem;
  }

  .dz-preview {
    .dz-image {
      border-radius: 0;

      img {
        object-fit: cover;
      }

      &:hover {
        img {
          filter: none;
          transform: none;
        }
      }
    }

    .dz-details {
      background-color: $info;
      bottom: 0;
      color: $white;
      text-align: left;
      top: 0;
      transition: opacity 0.2s linear;

      .dz-filename span,
      .dz-size span {
        background-color: transparent;
      }

      .dz-filename:not(:hover) span {
        border: none;
      }

      .dz-filename:hover span {
        background-color: transparent;
        border: none;
      }
    }

    .dz-progress .dz-upload {
      background: $grey;
    }

    .dz-remove {
      border: 2px $white solid;
      bottom: 15px;
      color: $white;
      font-size: 0.8rem;
      font-weight: $semibold;
      letter-spacing: 1.1px;
      margin-left: 15px;
      opacity: 0;
      padding: 10px;
      position: absolute;
      text-decoration: none;
      text-transform: uppercase;
      top: inherit;
      z-index: 30;
    }

    .dz-success-mark,
    .dz-error-mark {
      left: 0;
      margin-left: auto !important;
      margin-top: auto !important;
      top: 35% !important;
      width: 100% !important;

      i {
        color: $white !important;
        font-size: 5rem !important;
      }
    }

    .dz-error-message {
      left: calc(50% - 35px);
      top: calc(50% + 25px);
    }

    &:hover {
      .dz-remove {
        opacity: 1;
      }
    }
  }

  &:hover {
    background-color: $white-ter;
  }
}
</style>

<i18n>
{
  "en": {
    "cancel": "Cancel upload",
    "cancelConfirm": "Are you sure you want to cancel this upload?",
    "dropFiles": "Drop files here to upload",
    "ie": "Your browser does not support drag and drop file uploads.",
    "fallback": "Please use the fallback form below to upload your files like in the olden days.",
    "tooBig": "File is too big ({{filesize}}MiB). Max filesize: {{maxFilesize}}MiB.",
    "tooMany": "You can not upload any more files. (max: {{maxFiles}})",
    "invalidType": "You can't upload files of this type.",
    "remove": "Remove",
    "serverError": "Server responded with {{statusCode}} code."
  }
}
</i18n>
